import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add, GroupAdd, PersonRemove } from '@mui/icons-material';
import axios from 'axios';
import dayjs from 'dayjs';
import { useToast } from '../../contexts/ToastContext';

function employeeLabel(user) {
  if (!user) return '—';
  return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || '—';
}

/**
 * HRMS Bands — SUPER_ADMIN / HR_MANAGER create bands and assign
 * each employee to a single band. Existing staff: here. New hires: onboarding.
 */
function BandsPanel({
  employees = [],
  canManageBands,
  department,
  departments = []
}) {
  const { showToast } = useToast();
  const [bands, setBands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [selectedBand, setSelectedBand] = useState(null);
  const [createForm, setCreateForm] = useState({
    name: '',
    code: '',
    department: '',
    description: ''
  });
  const [memberUserId, setMemberUserId] = useState('');
  const [memberRemarks, setMemberRemarks] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { activeOnly: 'false' };
      if (department) params.department = department;
      const res = await axios.get('/api/hrms/bands', { params });
      setBands(res.data || []);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load bands', 'error');
      setBands([]);
    } finally {
      setLoading(false);
    }
  }, [department, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setCreateForm({
      name: '',
      code: '',
      department: department || '',
      description: ''
    });
    setCreateOpen(true);
  };

  const openMembers = async (band) => {
    try {
      const res = await axios.get(`/api/hrms/bands/${band._id}`);
      setSelectedBand(res.data);
      setMemberUserId('');
      setMemberRemarks('');
      setMembersOpen(true);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load band', 'error');
    }
  };

  const onCreate = async () => {
    if (!createForm.name.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: createForm.name,
        code: createForm.code || undefined,
        description: createForm.description || undefined,
        department: createForm.department || undefined
      };
      await axios.post('/api/hrms/bands', payload);
      showToast('Band created', 'success');
      setCreateOpen(false);
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Create failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (band) => {
    try {
      await axios.put(`/api/hrms/bands/${band._id}`, { isActive: !band.isActive });
      showToast(band.isActive ? 'Band deactivated' : 'Band activated', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Update failed', 'error');
    }
  };

  const eligibleEmployees = useMemo(() => {
    if (!selectedBand) return [];
    const memberIds = new Set(
      (selectedBand.members || []).map((m) => String(m.userId?._id || m.userId))
    );
    return employees.filter((e) => !memberIds.has(String(e._id)));
  }, [employees, selectedBand]);

  const onAssignMember = async () => {
    if (!selectedBand || !memberUserId) {
      showToast('Select an employee', 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`/api/hrms/bands/${selectedBand._id}/members`, {
        userId: memberUserId,
        remarks: memberRemarks
      });
      setSelectedBand(res.data);
      setMemberUserId('');
      setMemberRemarks('');
      showToast('Member assigned (moved from any previous band)', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Assign failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const onRemoveMember = async (userId) => {
    if (!selectedBand) return;
    try {
      const res = await axios.delete(
        `/api/hrms/bands/${selectedBand._id}/members/${userId}`
      );
      setSelectedBand(res.data);
      showToast('Member removed', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Remove failed', 'error');
    }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Super Admin and HR Manager create bands and assign each employee to one band.
        Assigning someone who is already in another band moves them to this band.
        Existing employees: assign here. New hires: optional band on People / User Onboarding.
      </Alert>

      {!canManageBands && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You can view bands you belong to. Only Super Admin or HR Manager can create bands or assign members.
        </Alert>
      )}

      <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
        {canManageBands && (
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
            Create band
          </Button>
        )}
        <Button onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </Stack>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Department tag</TableCell>
              <TableCell>Members</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created by</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {bands.map((band) => (
              <TableRow key={band._id} hover>
                <TableCell>
                  <Typography variant="body2">{band.name}</Typography>
                  {band.description ? (
                    <Typography variant="caption" color="text.secondary">
                      {band.description}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>{band.code || '—'}</TableCell>
                <TableCell>{band.department || '—'}</TableCell>
                <TableCell>{(band.members || []).length}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={band.isActive ? 'Active' : 'Inactive'}
                    color={band.isActive ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>{employeeLabel(band.createdBy)}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    <Button
                      size="small"
                      startIcon={<GroupAdd />}
                      onClick={() => openMembers(band)}
                    >
                      Members
                    </Button>
                    {canManageBands && (
                      <Button size="small" onClick={() => toggleActive(band)}>
                        {band.isActive ? 'Deactivate' : 'Activate'}
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!bands.length && (
              <TableRow>
                <TableCell colSpan={7}>
                  {loading ? 'Loading…' : 'No bands yet — HR Manager or Super Admin can create them'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create band</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              required
              fullWidth
              value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              placeholder="e.g. Band A, Safety Committee, L2"
            />
            <TextField
              label="Code"
              fullWidth
              value={createForm.code}
              onChange={(e) => setCreateForm({ ...createForm, code: e.target.value })}
              placeholder="Optional short code"
            />
            <FormControl fullWidth>
              <InputLabel>Department tag (optional)</InputLabel>
              <Select
                label="Department tag (optional)"
                value={createForm.department}
                onChange={(e) => setCreateForm({ ...createForm, department: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={2}
              value={createForm.description}
              onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onCreate} disabled={saving}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={membersOpen} onClose={() => setMembersOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          Band members
          {selectedBand ? ` · ${selectedBand.name}` : ''}
        </DialogTitle>
        <DialogContent>
          {selectedBand && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {canManageBands && selectedBand.isActive && (
                <>
                  <Typography variant="subtitle2">
                    Assign employee (one band only — moves them if already in another)
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
                    <FormControl fullWidth size="small">
                      <InputLabel>Employee</InputLabel>
                      <Select
                        label="Employee"
                        value={memberUserId}
                        onChange={(e) => setMemberUserId(e.target.value)}
                      >
                        {eligibleEmployees.map((e) => (
                          <MenuItem key={e._id} value={e._id}>
                            {e.firstName} {e.lastName}
                            {(e.bands || []).length
                              ? ` · currently in ${(e.bands || []).map((b) => b.name).join(', ')} (will move)`
                              : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      label="Remarks"
                      value={memberRemarks}
                      onChange={(e) => setMemberRemarks(e.target.value)}
                      sx={{ minWidth: 180 }}
                    />
                    <Button
                      variant="contained"
                      onClick={onAssignMember}
                      disabled={saving || !memberUserId}
                    >
                      Assign
                    </Button>
                  </Stack>
                  {!eligibleEmployees.length && (
                    <Typography variant="body2" color="text.secondary">
                      All listed employees are already members of this band.
                    </Typography>
                  )}
                  <Divider />
                </>
              )}

              <Typography variant="subtitle2">
                Current members ({(selectedBand.members || []).length})
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Assigned</TableCell>
                    <TableCell>By</TableCell>
                    <TableCell>Remarks</TableCell>
                    {canManageBands && <TableCell />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(selectedBand.members || []).map((m) => (
                    <TableRow key={m._id || String(m.userId?._id || m.userId)}>
                      <TableCell>{employeeLabel(m.userId)}</TableCell>
                      <TableCell>
                        {m.assignedAt ? dayjs(m.assignedAt).format('YYYY-MM-DD') : '—'}
                      </TableCell>
                      <TableCell>{employeeLabel(m.assignedBy)}</TableCell>
                      <TableCell>{m.remarks || '—'}</TableCell>
                      {canManageBands && (
                        <TableCell>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<PersonRemove />}
                            onClick={() => onRemoveMember(m.userId?._id || m.userId)}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {!(selectedBand.members || []).length && (
                    <TableRow>
                      <TableCell colSpan={canManageBands ? 5 : 4}>No members yet</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMembersOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BandsPanel;
